import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/**
 * Tool name -> what a person would call it. The difference between a product and a debug log.
 * select_company names the company it switched to, because a silently changed scope is the
 * highest-severity failure on the admin surface.
 */
const LABELS = {
  whoami: 'Checking your access',
  get_account_profile: 'Reading the account setup',
  search_everything: 'Searching',
  search_residents: 'Looking up residents',
  search_units: 'Looking up units',
  resolve_property: 'Finding the property',
  get_property_dashboard: 'Reading the property dashboard',
  get_allocation_status_summary: 'Checking allocation status',
  list_allocation_cycles: 'Finding the right billing cycle',
  get_allocation_audit: 'Running the utility allocation export',
  get_utility_allocation_export: 'Reading one cycle of the utility allocation export',
  get_reimbursement_kpis: 'Calculating recovery',
  get_accounts_receivable: 'Reading accounts receivable',
  get_convergent_charge_export: 'Reading the charge export',
  offer_report_email_change: 'Checking your report emails',
  get_reimbursement_report: 'Reading the reimbursement report',
  get_appfolio_batches: 'Reading pending charges',
  get_transactions: 'Reading the ledger',
  get_activity_logs: 'Reading the activity log',
  compare_companies: 'Comparing companies',
  select_company: 'Switching company',
  get_property: 'Reading the property',
  get_property_context: 'Reading the property',
  get_properties: 'Listing properties',
  list_properties_admin: 'Listing properties',
  get_companies: 'Listing companies',
  get_company: 'Reading the company',
  get_company_360: 'Reading the company',
  get_portfolios: 'Listing portfolios',
  get_buildings: 'Listing buildings',
  get_units: 'Listing units',
  get_residency: 'Reading the residency',
  get_residencies: 'Listing residencies',
  get_resident_360: 'Reading the resident',
  get_resident_details: 'Reading the resident',
  get_allocation_table: 'Reading the allocation table',
  get_current_allocation_cycle: 'Finding the current cycle',
  list_billing_cycles: 'Listing billing cycles',
  get_entity_history: 'Reading change history',
  get_recovery_summary: 'Calculating recovery',
  search_knowledge_base: 'Checking the knowledge base',
  get_kb_article: 'Reading a help article',
  propose_action: 'Preparing a change for you to confirm'
};

/**
 * Anything without a hand-written label still has to read as an action, not a function name:
 * "get property" in the middle of a sentence looks like the product leaking its internals.
 */
const VERBS = [[/^get_/, 'Reading'], [/^list_/, 'Listing'], [/^search_/, 'Searching'], [/^resolve_/, 'Finding'], [/^compare_/, 'Comparing'], [/^upload_/, 'Uploading'], [/^create_/, 'Creating'], [/^update_/, 'Updating']];
export const toolLabel = (name, company) => {
  if (name === 'select_company' && company) return "Switching to ".concat(company);
  const known = LABELS[name];
  if (known) return known;
  const verb = VERBS.find(_ref => {
    let _ref2 = _slicedToArray(_ref, 1),
      re = _ref2[0];
    return re.test(name);
  });
  const rest = verb ? name.replace(verb[0], '').replace(/_/g, ' ') : name.replace(/_/g, ' ');
  return verb ? "".concat(verb[1], " ").concat(rest) : rest.charAt(0).toUpperCase() + rest.slice(1);
};

/**
 * One turn can call the same tool once per property, which rendered as four identical chips and a
 * wall of noise. Collapsed to one entry each, in first-call order, with a count.
 */
export function summariseTools(names, company) {
  const order = [];
  const counts = new Map();
  for (const name of names) {
    var _counts$get;
    const label = toolLabel(name, company);
    if (!counts.has(label)) order.push(label);
    counts.set(label, ((_counts$get = counts.get(label)) !== null && _counts$get !== void 0 ? _counts$get : 0) + 1);
  }
  return order.map(label => ({
    label,
    count: counts.get(label)
  }));
}